import React, { useEffect, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon, Plus, Check } from "lucide-react";
import reqComplementos from "../../../services/reqMultiTenant/reqComplementos";
import libFloat from "../../../util/libFloat";
import Notification from "../../toast";

// Passo 2 do seletor de produto: grupos de complementos (Normal / Subitem / Grade) do
// produto escolhido. Espelha app/products/[id]/_components/product-complements.tsx do
// sz-foods — mesmas regras de seleção, sem a parte visual de fotos grandes.
export default function ProductComplementsSelect({
	produto,
	onSelectionChange,
	onValidationChange,
	onHasSubitemChange,
	onHasGradeChange,
	onHasAnyComplementsChange,
}) {
	const [grupos, setGrupos] = useState([]);
	const [loading, setLoading] = useState(true);
	const [selecionados, setSelecionados] = useState(new Map()); // id_complementos_item -> quantidade

	const handleToggle = (item, grupo) => {
		setSelecionados((prev) => {
			const next = new Map(prev);
			if (next.has(item.id_complementos_item)) {
				next.delete(item.id_complementos_item);
			} else {
				if (grupo.qtde_max_escolha > 0) {
					const totalNoGrupo = grupo.itens.reduce(
						(acc, gi) => acc + (next.get(gi.id_complementos_item) || 0),
						0,
					);
					if (totalNoGrupo >= grupo.qtde_max_escolha) return prev;
				}
				next.set(item.id_complementos_item, 1);
			}
			return next;
		});
	};

	const handleIncrement = (item, grupo) => {
		setSelecionados((prev) => {
			const next = new Map(prev);
			const atual = next.get(item.id_complementos_item) || 0;

			if (grupo.qtde_max_escolha > 0) {
				const totalNoGrupo = grupo.itens.reduce(
					(acc, gi) => acc + (next.get(gi.id_complementos_item) || 0),
					0,
				);
				if (totalNoGrupo >= grupo.qtde_max_escolha) return prev;
			}

			next.set(item.id_complementos_item, atual + 1);
			return next;
		});
	};

	const handleDecrement = (item) => {
		setSelecionados((prev) => {
			const next = new Map(prev);
			const atual = next.get(item.id_complementos_item) || 0;
			if (atual <= 1) {
				next.delete(item.id_complementos_item);
			} else {
				next.set(item.id_complementos_item, atual - 1);
			}
			return next;
		});
	};

	// Busca os grupos de complementos do produto
	useEffect(() => {
		if (!produto?.id_produto) return;

		const fetchComplementos = async () => {
			setLoading(true);
			try {
				const response = await reqComplementos.listarComplementosProduto(produto.id_produto);
				const data = response.data || [];

				const agrupado = data.reduce((acc, curr) => {
					const groupId = curr.id_complementos_grupo;
					if (!acc[groupId]) {
						acc[groupId] = {
							id: groupId,
							nome: curr.grupo_descricao,
							obrigatorio: curr.obrigatorio,
							qtde_max_escolha: curr.qtde_max_escolha,
							ordem: curr.grupo_ordem,
							subitem: !!curr.grupo_subitem,
							is_grade: !!curr.is_grade,
							itens: [],
						};
					}
					acc[groupId].itens.push({
						id_complementos_item: curr.id_complementos_item,
						id_produto_item: curr.id_produto_item,
						nome_produto_item: curr.nome_produto_item,
						valor_item: Number(curr.valor_item) || 0,
						ordem: curr.item_ordem,
						qtatual: curr.qtatual ?? 1,
					});
					return acc;
				}, {});

				const listaOrdenada = Object.values(agrupado)
					.sort((a, b) => a.ordem - b.ordem)
					.map((g) => ({ ...g, itens: g.itens.sort((a, b) => a.ordem - b.ordem) }));

				setGrupos(listaOrdenada);
				if (onHasSubitemChange) onHasSubitemChange(listaOrdenada.some((g) => g.subitem));
				if (onHasGradeChange) onHasGradeChange(listaOrdenada.some((g) => g.is_grade));
				if (onHasAnyComplementsChange) onHasAnyComplementsChange(listaOrdenada.length > 0);
			} catch (error) {
				Notification.showToast({ message: "Erro ao carregar complementos do produto.", type: "error" });
				setGrupos([]);
			} finally {
				setLoading(false);
			}
		};

		fetchComplementos();
	}, [produto?.id_produto]);

	// Notifica o pai sempre que a seleção mudar
	useEffect(() => {
		const lista = [];
		let valido = true;

		grupos.forEach((grupo) => {
			let totalNoGrupo = 0;

			grupo.itens.forEach((item) => {
				const qtd = selecionados.get(item.id_complementos_item);
				if (qtd && qtd > 0) {
					totalNoGrupo += qtd;
					lista.push({
						id_produto: item.id_produto_item,
						id_complementos_item: item.id_complementos_item,
						id_complementos_grupo: grupo.id,
						nome_produto_item: item.nome_produto_item,
						valor_item: item.valor_item,
						quantidade: qtd,
						valor_total: item.valor_item * qtd,
						subitem: grupo.subitem,
						is_grade: grupo.is_grade,
					});
				}
			});

			if (grupo.obrigatorio && totalNoGrupo === 0) {
				valido = false;
			}
		});

		if (onSelectionChange) onSelectionChange(lista);
		if (onValidationChange) onValidationChange(valido);
	}, [selecionados, grupos]);

	if (loading) {
		return <div className="p-6 text-center text-sm text-gray-500">Carregando complementos...</div>;
	}

	if (grupos.length === 0) return null;

	return (
		<div className="space-y-3">
			{grupos.map((grupo) => {
				const totalNoGrupo = grupo.itens.reduce(
					(acc, gi) => acc + (selecionados.get(gi.id_complementos_item) || 0),
					0,
				);
				const maxAtingido = grupo.qtde_max_escolha > 0 && totalNoGrupo >= grupo.qtde_max_escolha;
				const grupoInvalido = grupo.obrigatorio && totalNoGrupo === 0;

				return (
					<div
						key={grupo.id}
						className={`overflow-hidden rounded-2xl border ${grupoInvalido ? "border-red-200" : "border-gray-100"}`}
					>
						<div className="flex items-center justify-between bg-gray-50 px-4 py-2.5">
							<div>
								<div className="flex items-center gap-2">
									<span className="text-sm font-bold text-gray-800">{grupo.nome}</span>
									{grupo.obrigatorio && (
										<span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-red-600">
											obrigatório
										</span>
									)}
									{grupo.is_grade && (
										<span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-bold uppercase text-purple-600">
											grade
										</span>
									)}
								</div>
								<p className="text-xs text-gray-500">
									{grupo.qtde_max_escolha > 0
										? `Escolha até ${grupo.qtde_max_escolha}`
										: "Escolha quantos quiser"}
								</p>
							</div>
							{grupo.qtde_max_escolha > 0 && (
								<span className="text-xs font-bold text-gray-500">
									{totalNoGrupo}/{grupo.qtde_max_escolha}
								</span>
							)}
						</div>

						<div className="divide-y divide-gray-50">
							{grupo.itens.map((item) => {
								const qtd = selecionados.get(item.id_complementos_item) || 0;
								const selecionado = qtd > 0;
								const desabilitado = !selecionado && maxAtingido;

								return (
									<div
										key={item.id_complementos_item}
										className={`flex items-center justify-between gap-3 px-4 py-3 transition-colors ${
											selecionado ? "bg-blue-50/50" : desabilitado ? "opacity-40" : "hover:bg-blue-50/40"
										}`}
									>
										{/* Botão de verdade: focável por Tab, ativa com Enter/Espaço nativamente. Ícone
										    (+ / check) deixa explícito que a linha inteira é clicável, não só um texto. */}
										<button
											type="button"
											disabled={desabilitado}
											onClick={() => handleToggle(item, grupo)}
											className="group flex min-w-0 flex-1 items-center gap-3 text-left disabled:cursor-not-allowed"
										>
											<div
												className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
													selecionado
														? "border-blue-600 bg-blue-600 text-white"
														: "border-dashed border-gray-300 text-gray-400 group-hover:border-blue-400 group-hover:text-blue-500"
												}`}
											>
												{selecionado ? <Check size={14} strokeWidth={3} /> : <Plus size={14} strokeWidth={3} />}
											</div>
											<span className="truncate text-sm font-medium text-gray-700">{item.nome_produto_item}</span>
										</button>

										<div className="flex flex-shrink-0 items-center gap-3">
											{item.valor_item > 0 && (
												<span className="text-xs font-bold text-gray-600">
													{libFloat.strToCurrency(item.valor_item)}
												</span>
											)}
											{selecionado && (
												<div
													className="flex items-center gap-1 rounded-full border border-gray-200 bg-white p-0.5"
													onClick={(e) => e.stopPropagation()}
												>
													<button
														type="button"
														onClick={() => handleDecrement(item)}
														className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-gray-100"
													>
														<ChevronLeftIcon size={14} />
													</button>
													<span className="w-4 text-center text-xs font-bold">{qtd}</span>
													<button
														type="button"
														disabled={maxAtingido}
														onClick={() => handleIncrement(item, grupo)}
														className={`flex h-6 w-6 items-center justify-center rounded-full ${
															maxAtingido ? "opacity-30" : "hover:bg-gray-100"
														}`}
													>
														<ChevronRightIcon size={14} />
													</button>
												</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				);
			})}
		</div>
	);
}
